import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Trash2, Send, ListChecks, Sparkles } from "lucide-react";
import { useApp, API } from "@/contexts/AppContext";
import LucideIcon from "@/components/LucideIcon";
import SectionHeading from "@/components/SectionHeading";
import { BUILDER } from "@/constants/testIds";
import { toast } from "sonner";

export default function BuilderPage() {
  const {
    scenarios,
    selectedScenarios,
    toggleScenario,
    clearBuilder,
    requiredProducts,
    localized,
    t,
  } = useApp();

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t.contact.error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        scenarios: selectedScenarios,
        products: requiredProducts.map((p) => p.slug),
      });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid={BUILDER.root}
      className="max-w-7xl mx-auto px-5 sm:px-10 py-16 sm:py-24"
    >
      <SectionHeading
        label={t.cta.label}
        title={t.builder.title}
        subtitle={t.builder.subtitle}
      />

      {/* Step 1 */}
      <div className="mt-14">
        <StepTitle index="01" title={t.builder.stepScenarios} />
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((sc) => {
            const selected = selectedScenarios.includes(sc.slug);
            return (
              <button
                key={sc.slug}
                data-testid={BUILDER.toggleScenario(sc.slug)}
                onClick={() => toggleScenario(sc.slug)}
                className={`text-left rounded-2xl border p-5 transition-all ${
                  selected
                    ? "border-amber-500/60 bg-amber-500/[0.06] shadow-[0_0_30px_rgba(245,158,11,0.08)]"
                    : "border-white/[0.07] bg-[#0B0F19] hover:border-white/20"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${sc.color}1A`,
                      border: `1px solid ${sc.color}55`,
                      color: sc.color,
                    }}
                  >
                    <LucideIcon name={sc.icon} className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-medium text-white text-lg leading-tight">
                      {localized(sc, "title")}
                    </div>
                    <div className="text-sm text-gray-400 mt-1 line-clamp-2">
                      {localized(sc, "tagline")}
                    </div>
                  </div>
                  <div
                    className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center border transition-colors ${
                      selected
                        ? "bg-amber-500 border-amber-500 text-black"
                        : "border-white/20"
                    }`}
                  >
                    {selected && <Check className="h-3.5 w-3.5" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2 */}
      <div className="mt-20">
        <StepTitle index="02" title={t.builder.stepList} />
        <div className="mt-6 rounded-3xl border border-white/[0.07] bg-[#0B0F19] p-6 sm:p-10">
          <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
            <div className="flex items-center gap-4">
              <Counter
                testId={BUILDER.scenarioCount}
                value={selectedScenarios.length}
                label={t.builder.scenariosSelected}
                color="#F59E0B"
                icon={<Sparkles className="h-4 w-4" />}
              />
              <Counter
                testId={BUILDER.productCount}
                value={requiredProducts.length}
                label={t.builder.productsNeeded}
                color="#10B981"
                icon={<ListChecks className="h-4 w-4" />}
              />
            </div>
            {selectedScenarios.length > 0 && (
              <button
                data-testid={BUILDER.clearBtn}
                onClick={() => {
                  clearBuilder();
                  toast(t.builder.clear);
                }}
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                {t.builder.clear}
              </button>
            )}
          </div>

          {selectedScenarios.length === 0 ? (
            <div className="text-gray-500 text-sm text-center py-12 border border-dashed border-white/10 rounded-2xl">
              {t.builder.empty}
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {requiredProducts.map((p) => (
                  <motion.div
                    key={p.slug}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    data-testid={BUILDER.productItem(p.slug)}
                    className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-[#11151F] p-4"
                  >
                    <div className="h-9 w-9 flex-shrink-0 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-300">
                      <LucideIcon name={p.icon} className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-medium text-white text-sm">
                        {localized(p, "title")}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {localized(p, "description")}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Step 3 */}
      <div className="mt-20">
        <StepTitle index="03" title={t.builder.stepForm} />
        <form
          onSubmit={submit}
          className="mt-6 rounded-3xl border border-white/[0.07] bg-[#0B0F19] p-6 sm:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
        >
          <Field
            testId={BUILDER.nameInput}
            value={form.name}
            onChange={(v) => update("name", v)}
            label={t.contact.name}
            required
          />
          <Field
            testId={BUILDER.emailInput}
            type="email"
            value={form.email}
            onChange={(v) => update("email", v)}
            label={t.contact.email}
            required
          />
          <Field
            testId={BUILDER.phoneInput}
            value={form.phone}
            onChange={(v) => update("phone", v)}
            label={t.contact.phone}
          />
          <div className="md:col-span-2">
            <Field
              testId={BUILDER.messageInput}
              value={form.message}
              onChange={(v) => update("message", v)}
              label={t.contact.message}
              textarea
            />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              data-testid={BUILDER.submitBtn}
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-7 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(245,158,11,0.45)] disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {t.builder.sendQuote}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function StepTitle({ index, title }) {
  return (
    <div className="flex items-baseline gap-4">
      <span className="font-mono-tight text-amber-400 text-sm">{index}</span>
      <h3 className="font-heading font-medium text-xl sm:text-2xl tracking-tight">
        {title}
      </h3>
    </div>
  );
}

function Counter({ testId, value, label, color, icon }) {
  return (
    <div data-testid={testId} className="flex items-center gap-3">
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center"
        style={{ background: `${color}1A`, border: `1px solid ${color}55`, color }}
      >
        {icon}
      </div>
      <div>
        <div className="font-mono-tight text-2xl font-medium text-white leading-none">
          {String(value).padStart(2, "0")}
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mt-1">
          {label}
        </div>
      </div>
    </div>
  );
}

function Field({ testId, label, value, onChange, type = "text", required, textarea }) {
  const baseClass =
    "w-full bg-[#11151F] border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-amber-500/60 focus:ring-2 focus:ring-amber-500/15 transition-colors";
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2 font-bold">
        {label}
      </span>
      {textarea ? (
        <textarea
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          required={required}
          className={baseClass + " resize-none"}
        />
      ) : (
        <input
          data-testid={testId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className={baseClass}
        />
      )}
    </label>
  );
}
